<template>
  <div class="modal-overlay" @click.self="$emit('close')" @mousedown.stop @mouseup.stop>
    <div class="modal-container bulk-import-modal" @click.stop>
      <div class="modal-header">
        <div class="modal-header-content">
          <h2>{{ $t('vendors.bulkImportTitle', 'Import Vendors from Excel') }}</h2>
        </div>
        <button @click="$emit('close')" class="btn btn-icon">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <div class="modal-body">
        <!-- Instructions -->
        <div class="import-instructions">
          <div class="instruction-icon">
            <font-awesome-icon icon="fa-solid fa-lightbulb" />
          </div>
          <div class="instruction-text">
            <h4>{{ $t('vendors.importInstructions', 'How to import') }}:</h4>
            <ul>
              <li>{{ $t('vendors.importStep1', 'Prepare your Excel file with vendor data (Name column is required)') }}</li>
              <li>{{ $t('vendors.importStep2', 'Upload the Excel file below') }}</li>
              <li>{{ $t('vendors.importStep3', 'Verify the detected columns and adjust if needed') }}</li>
              <li>{{ $t('vendors.importStep4', 'Review the preview and import') }}</li>
            </ul>
          </div>
        </div>

        <!-- File Upload -->
        <div v-if="!importing && !importResult" class="form-group">
          <label class="form-label">
            {{ $t('vendors.selectExcelFile', 'Select Excel file') }}
            <span class="required">*</span>
          </label>

          <div
            class="file-upload-area"
            :class="{ 'drag-over': dragActive }"
            @drop.prevent="handleFileDrop"
            @dragover.prevent="dragActive = true"
            @dragleave.prevent="dragActive = false"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              @change="handleFileSelect"
              :disabled="parsing || importing"
              style="display: none"
            />

            <div v-if="!excelFile" class="upload-prompt" @click="$refs.fileInput.click()">
              <font-awesome-icon icon="fa-solid fa-file-excel" class="upload-icon" />
              <p class="upload-text">{{ $t('vendors.dropFileHere', 'Drop Excel file here or click to browse') }}</p>
              <p class="upload-hint">{{ $t('vendors.supportedFormats', 'Supports .xlsx and .xls files (max 10MB)') }}</p>
            </div>

            <div v-else class="file-selected">
              <font-awesome-icon icon="fa-solid fa-file-excel" class="file-icon" />
              <div class="file-info">
                <p class="file-name">{{ excelFile.name }}</p>
                <p class="file-size">{{ formatFileSize(excelFile.size) }}</p>
              </div>
              <button @click.stop="removeFile" class="btn-remove-file">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
        </div>

        <!-- Column mapping -->
        <div v-if="!importing && !importResult && excelData.length > 0" class="form-group">
          <label class="form-label">{{ $t('vendors.mapColumns', 'Map Columns') }}</label>
          <div class="column-mapping">
            <div class="mapping-row">
              <label>{{ $t('vendors.vendorNameColumn', 'Vendor Name Column') }} <span class="required">*</span></label>
              <select v-model="columnMapping.vendor_name" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('vendors.vendorCodeColumn', 'Vendor Code Column') }}</label>
              <select v-model="columnMapping.vendor_code" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('vendors.vatColumn', 'VAT Number Column') }}</label>
              <select v-model="columnMapping.vendor_vat" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('vendors.taxIdColumn', 'Tax ID / Bulstat Column') }}</label>
              <select v-model="columnMapping.vendor_tax_id" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('vendors.addressColumn', 'Address Column') }}</label>
              <select v-model="columnMapping.vendor_address" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('vendors.phoneColumn', 'Phone Column') }}</label>
              <select v-model="columnMapping.vendor_phone" class="form-input">
                <option value="">-- {{ $t('vendors.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Preview (auto-detected columns) -->
        <div v-if="!importing && !importResult && excelData.length > 0" class="form-group">
          <div v-if="columnMapping.vendor_name !== ''" class="data-preview">
            <h4>
              <font-awesome-icon icon="fa-solid fa-eye" />
              {{ $t('vendors.dataPreview', 'Data Preview') }} ({{ $t('vendors.first5Rows', 'First 5 rows') }})
            </h4>
            <div class="preview-table-wrapper">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>{{ $t('vendors.vendorName', 'Vendor Name') }}</th>
                    <th v-if="columnMapping.vendor_code !== ''">{{ $t('vendors.vendorCode', 'Code') }}</th>
                    <th v-if="columnMapping.vendor_vat !== ''">{{ $t('vendors.vatNumber', 'VAT') }}</th>
                    <th v-if="columnMapping.vendor_tax_id !== ''">{{ $t('vendors.taxId', 'Tax ID') }}</th>
                    <th v-if="columnMapping.vendor_address !== ''">{{ $t('vendors.address', 'Address') }}</th>
                    <th v-if="columnMapping.vendor_email !== ''">{{ $t('vendors.email', 'Email') }}</th>
                    <th v-if="columnMapping.vendor_phone !== ''">{{ $t('vendors.phone', 'Phone') }}</th>
                    <th v-if="columnMapping.vendor_type !== ''">{{ $t('vendors.type', 'Type') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in previewData" :key="idx">
                    <td>{{ row.vendor_name }}</td>
                    <td v-if="columnMapping.vendor_code !== ''">{{ row.vendor_code }}</td>
                    <td v-if="columnMapping.vendor_vat !== ''">{{ row.vendor_vat }}</td>
                    <td v-if="columnMapping.vendor_tax_id !== ''">{{ row.vendor_tax_id }}</td>
                    <td v-if="columnMapping.vendor_address !== ''">{{ row.vendor_address }}</td>
                    <td v-if="columnMapping.vendor_email !== ''">{{ row.vendor_email }}</td>
                    <td v-if="columnMapping.vendor_phone !== ''">{{ row.vendor_phone }}</td>
                    <td v-if="columnMapping.vendor_type !== ''">{{ row.vendor_type }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="error-message">
            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
            {{ $t('vendors.noVendorNameColumn', 'Could not detect a "Vendor Name" column. Make sure your file has a header row with a column named "Vendor Name", "Име", or similar.') }}
          </div>
        </div>

        <!-- Options -->
        <div v-if="!importing && !importResult && excelData.length > 0" class="form-group">
          <label class="form-label">{{ $t('vendors.importOptions', 'Import Options') }}</label>
          <div class="radio-group">
            <label class="radio-label">
              <input type="checkbox" v-model="skipDuplicates" />
              <span>
                {{ $t('vendors.skipDuplicates', 'Skip Duplicates') }}
                <small>{{ $t('vendors.skipDuplicatesHint', 'Existing vendors (by VAT/Tax ID/Code) will be skipped') }}</small>
              </span>
            </label>
            <label class="radio-label">
              <input type="checkbox" v-model="updateExisting" />
              <span>
                {{ $t('vendors.updateExisting', 'Update Existing') }}
                <small>{{ $t('vendors.updateExistingHint', 'Update information for existing vendors') }}</small>
              </span>
            </label>
          </div>
        </div>

        <!-- Parse error -->
        <div v-if="parseError" class="error-message">
          <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
          {{ parseError }}
        </div>

        <!-- Action -->
        <div v-if="!importing && !importResult" class="action-buttons">
          <button
            @click="startImport"
            :disabled="!canImport"
            class="btn-primary btn-large"
          >
            <font-awesome-icon icon="fa-solid fa-file-import" />
            {{ $t('vendors.import', 'Import Vendors') }}
          </button>
        </div>

        <!-- Importing -->
        <div v-if="importing" class="import-progress">
          <div class="spinner"></div>
          <p>{{ $t('vendors.importing', 'Importing vendors...') }}</p>
        </div>

        <!-- Result -->
        <div v-if="importResult" class="import-result" :class="importResult.success ? 'success' : 'error'">
          <font-awesome-icon
            :icon="importResult.success ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'"
          />
          <div style="flex: 1;">
            <p class="result-message">{{ importResult.message }}</p>
            <div class="result-stats">
              <span v-if="importResult.imported > 0" class="stat-chip success">
                {{ importResult.imported }} {{ $t('vendors.imported', 'Imported') }}
              </span>
              <span v-if="importResult.updated > 0" class="stat-chip info">
                {{ importResult.updated }} {{ $t('vendors.updated', 'Updated') }}
              </span>
              <span v-if="importResult.skipped > 0" class="stat-chip warning">
                {{ importResult.skipped }} {{ $t('vendors.skipped', 'Skipped') }}
              </span>
              <span v-if="importResult.errors && importResult.errors.length > 0" class="stat-chip error">
                {{ importResult.errors.length }} {{ $t('vendors.errors', 'Errors') }}
              </span>
            </div>
            <ul v-if="importResult.errors && importResult.errors.length > 0" class="error-list">
              <li v-for="(error, index) in importResult.errors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>

        <div v-if="importResult" class="action-buttons">
          <button v-if="importResult.success" @click="resetImport" class="btn-secondary">
            {{ $t('vendors.importMore', 'Import More') }}
          </button>
          <button @click="$emit('close')" class="btn-primary btn-large">
            {{ $t('common.close', 'Close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import * as XLSX from 'xlsx';
import api from '@/utils/api';

const props = defineProps({
  companyId: {
    type: [String, Number],
    required: true
  }
});

const emit = defineEmits(['close', 'imported']);

const { t } = useI18n();

const fileInput = ref(null);
const excelFile = ref(null);
const excelColumns = ref([]);
const excelData = ref([]);
const dragActive = ref(false);
const parseError = ref(null);
const importing = ref(false);
const importResult = ref(null);
const skipDuplicates = ref(true);
const updateExisting = ref(false);

const columnMapping = ref({
  vendor_name: '',
  vendor_code: '',
  vendor_vat: '',
  vendor_tax_id: '',
  vendor_address: '',
  vendor_email: '',
  vendor_phone: '',
  vendor_type: ''
});

const canImport = computed(() => {
  return excelData.value.length > 0 && columnMapping.value.vendor_name !== '';
});

const previewData = computed(() => {
  return excelData.value.slice(0, 5).map(row => buildVendorRow(row));
});

const buildVendorRow = (row) => {
  const result = {};
  for (const field of Object.keys(columnMapping.value)) {
    const idx = columnMapping.value[field];
    if (idx === '' || idx === null || idx === undefined) continue;
    const value = row[idx];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      result[field] = String(value).trim();
    }
  }
  return result;
};

const isExcelFile = (file) => {
  if (!file) return false;
  if (/\.(xlsx|xls)$/i.test(file.name)) return true;
  const excelMimes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/x-excel',
    'application/x-msexcel'
  ];
  return excelMimes.includes(file.type);
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  if (!isExcelFile(file)) {
    parseError.value = t('vendors.invalidFileType', 'Please select a valid Excel file (.xlsx or .xls)');
    return;
  }
  processExcelFile(file);
};

const handleFileDrop = (event) => {
  dragActive.value = false;
  const file = event.dataTransfer.files[0];
  if (!file) return;
  if (!isExcelFile(file)) {
    parseError.value = t('vendors.invalidFileType', 'Please select a valid Excel file (.xlsx or .xls)');
    return;
  }
  processExcelFile(file);
};

const processExcelFile = (file) => {
  if (file.size > 10 * 1024 * 1024) {
    parseError.value = t('vendors.fileTooLarge', 'File is too large. Maximum size: 10MB');
    return;
  }

  excelFile.value = file;
  parseError.value = null;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      if (jsonData.length === 0) {
        parseError.value = t('vendors.emptyFile', 'Excel file is empty');
        return;
      }

      excelColumns.value = jsonData[0].map((col, idx) => col || `Column ${idx + 1}`);
      excelData.value = jsonData.slice(1).filter(row => row.some(cell => cell !== '' && cell !== null));
      autoDetectColumns();
    } catch (error) {
      parseError.value = t('vendors.readError', 'Failed to read Excel file: ') + error.message;
      console.error('Excel parsing error:', error);
    }
  };

  reader.readAsArrayBuffer(file);
};

const autoDetectColumns = () => {
  const headers = excelColumns.value.map(h => (h || '').toString().toLowerCase().trim());

  const detect = (patterns) => headers.findIndex(h => patterns.some(p => h.includes(p)));

  const nameIdx = detect(['vendor name', 'name', 'име на доставчик', 'име', 'доставчик']);
  if (nameIdx !== -1) columnMapping.value.vendor_name = nameIdx;

  const codeIdx = detect(['vendor code', 'code', 'код на доставчик', 'код']);
  if (codeIdx !== -1) columnMapping.value.vendor_code = codeIdx;

  const vatIdx = detect(['vat number', 'vat', 'ддс номер', 'ддс']);
  if (vatIdx !== -1) columnMapping.value.vendor_vat = vatIdx;

  const taxIdx = detect(['tax id', 'bulstat', 'еик', 'булстат']);
  if (taxIdx !== -1) columnMapping.value.vendor_tax_id = taxIdx;

  const addrIdx = detect(['address', 'адрес']);
  if (addrIdx !== -1) columnMapping.value.vendor_address = addrIdx;

  const emailIdx = detect(['e-mail', 'email', 'имейл']);
  if (emailIdx !== -1) columnMapping.value.vendor_email = emailIdx;

  const phoneIdx = detect(['phone', 'телефон', 'тел.', 'тел']);
  if (phoneIdx !== -1) columnMapping.value.vendor_phone = phoneIdx;

  const typeIdx = detect(['type', 'тип']);
  if (typeIdx !== -1) columnMapping.value.vendor_type = typeIdx;
};

const removeFile = () => {
  excelFile.value = null;
  excelColumns.value = [];
  excelData.value = [];
  parseError.value = null;
  columnMapping.value = {
    vendor_name: '',
    vendor_code: '',
    vendor_vat: '',
    vendor_tax_id: '',
    vendor_address: '',
    vendor_email: '',
    vendor_phone: '',
    vendor_type: ''
  };
  if (fileInput.value) fileInput.value.value = '';
};

const getColumnLetter = (index) => {
  let letter = '';
  let num = index;
  while (num >= 0) {
    letter = String.fromCharCode(65 + (num % 26)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const startImport = async () => {
  if (!canImport.value) return;

  const vendors = excelData.value
    .map(row => buildVendorRow(row))
    .filter(v => v.vendor_name);

  if (vendors.length === 0) {
    parseError.value = t('vendors.noValidRows', 'No valid vendor rows found after mapping');
    return;
  }

  importing.value = true;
  parseError.value = null;

  try {
    const response = await api.post(
      `/api/companies/${props.companyId}/vendors/bulk-import`,
      {
        vendors,
        skip_duplicates: skipDuplicates.value,
        update_existing: updateExisting.value
      }
    );

    importResult.value = response.data;
    importing.value = false;

    if (response.data.success && response.data.imported > 0) {
      emit('imported');
    }
  } catch (error) {
    console.error('Import error:', error);
    importing.value = false;

    importResult.value = error.response?.data || {
      success: false,
      message: t('vendors.importFailed', 'Failed to import vendors'),
      imported: 0,
      updated: 0,
      skipped: 0,
      errors: [error.message || t('vendors.unknownError', 'Unknown error')]
    };
  }
};

const resetImport = () => {
  removeFile();
  importing.value = false;
  importResult.value = null;
  skipDuplicates.value = true;
  updateExisting.value = false;
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
}

.bulk-import-modal {
  max-width: 900px;
  margin: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
  margin-left: 4px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.import-instructions {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%);
  border-left: 4px solid var(--primary);
  border-radius: 8px;
  margin-bottom: 24px;
}

.instruction-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  border-radius: 50%;
  font-size: 20px;
}

.instruction-text h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.instruction-text ul {
  margin: 0;
  padding-left: 20px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.6;
}

.instruction-text li {
  margin-bottom: 4px;
}

/* Checkboxes / options */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
  padding: 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.radio-label:hover {
  border-color: var(--primary);
  background-color: rgba(14, 165, 233, 0.05);
}

.radio-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--primary);
  margin-top: 2px;
}

.radio-label span {
  display: block;
  line-height: 1.4;
}

.radio-label small {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: normal;
  color: var(--text-secondary);
}

/* File upload */
.file-upload-area {
  border: 2px dashed var(--border);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  background: var(--bg-gray);
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: var(--primary);
  background: rgba(14, 165, 233, 0.05);
}

.file-upload-area.drag-over {
  border-color: var(--primary);
  background: rgba(14, 165, 233, 0.1);
  transform: scale(1.02);
}

.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  font-size: 48px;
  color: #10b981;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.upload-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  text-align: left;
  cursor: default;
}

.file-icon {
  font-size: 36px;
  color: #10b981;
  flex-shrink: 0;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  word-break: break-word;
}

.file-size {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-remove-file {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  border: none;
  border-radius: 6px;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-remove-file:hover {
  background: #fee2e2;
  transform: scale(1.1);
}

/* Column mapping */
.column-mapping {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 20px;
  background: var(--bg-gray);
  border-radius: 10px;
  margin-bottom: 20px;
}

.mapping-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mapping-row label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.column-mapping-help {
  margin-bottom: 16px;
  padding: 16px;
  background: #eff6ff;
  border-left: 4px solid var(--primary);
  border-radius: 8px;
}

.column-mapping-help p {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: var(--text-primary);
}

.column-mapping-help ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.column-mapping-help li {
  font-size: 13px;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.column-mapping-help strong {
  color: var(--text-primary);
  font-weight: 600;
}

/* Data Preview */
.data-preview {
  margin-top: 24px;
  padding: 20px;
  background: white;
  border: 2px solid var(--border);
  border-radius: 10px;
}

.data-preview h4 {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-table-wrapper {
  max-height: 250px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.preview-table thead {
  position: sticky;
  top: 0;
  background: var(--bg-gray);
  z-index: 1;
}

.preview-table th {
  padding: 10px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border);
  white-space: nowrap;
}

.preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
}

.preview-table tbody tr:hover {
  background: #f8fafc;
}

/* Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: var(--text-primary);
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.btn-large {
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

/* Error / spinner / result */
.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  border-radius: 8px;
  color: #dc2626;
  margin-top: 16px;
  font-size: 14px;
}

.import-progress {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.import-result {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
}

.import-result.success {
  background: #d1fae5;
  border-left: 4px solid #10b981;
  color: #065f46;
}

.import-result.error {
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  color: #dc2626;
}

.import-result svg {
  flex-shrink: 0;
  font-size: 20px;
  margin-top: 2px;
}

.result-message {
  font-weight: 600;
  margin: 0 0 8px 0;
}

.result-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.stat-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.stat-chip.success { background: #d1fae5; color: #059669; }
.stat-chip.info { background: #dbeafe; color: #0284c7; }
.stat-chip.warning { background: #fef3c7; color: #d97706; }
.stat-chip.error { background: #fecaca; color: #dc2626; }

.error-list {
  margin: 8px 0 0 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
}

.error-list li {
  margin-bottom: 4px;
}

@media (max-width: 640px) {
  .column-mapping {
    grid-template-columns: 1fr;
  }
}
</style>
